import React, { Fragment, HtmlHTMLAttributes, memo, ReactNode, useCallback, useMemo, useRef, useState } from "react";
import { useGetCheckAccessFunction } from "../../core";
import { Button, ButtonProps } from "../Button";
import { Dropdown, DropdownListItem, DropdownListItemProps, DropdownProps } from "../Dropdown";

import { ReactComponent as DotsIcon } from "./icons/dropdown.svg";

export type AdditionalActionDescription<P extends object = HtmlHTMLAttributes<HTMLButtonElement>> =
  {
    id: string,
    requiredPermissions?: string | string[]
    requiredOneOfPermissions?: string | string[],
    additionalContent?: ReactNode | ReactNode[],
  } & DropdownListItemProps<P>;

export type AdditionalActionsProps = Omit<DropdownProps, "isOpen" | "referenceElement" | "onRequestClose"> & {
  buttonProps?: Omit<ButtonProps, "onClick">
  actions?: AdditionalActionDescription[],
};

export const AdditionalActions = memo<AdditionalActionsProps>(({ buttonProps, actions, ...dropDownProps }) => {
  const [ isOpen, setIsOpen ] = useState(false);
  const refObject = useRef<HTMLButtonElement>(null);
  const toggleState = useCallback(() => setIsOpen(prevState => !prevState), []);
  const checkAccessFunction = useGetCheckAccessFunction();

  const filteredActions = useMemo(() => actions?.filter(x => {
    const { isAccessAllowed, pending } = checkAccessFunction();
    return !pending && isAccessAllowed;
  }), [ actions, checkAccessFunction ]);

  if (!filteredActions?.length) {
    return null;
  }

  return (
    <>
      <Button
        buttonType="linkGray"
        children={<DotsIcon />}
        {...buttonProps}
        ref={refObject}
        onClick={toggleState}
      />
      <Dropdown
        {...dropDownProps}
        floatingOptions={{
          placement: "bottom-end",
          ...dropDownProps.floatingOptions,
        }}
        isOpen={isOpen}
        referenceElement={refObject?.current}
        onRequestClose={toggleState}
      >
        {
          filteredActions.map((
            {
              id,
              requiredPermissions,
              requiredOneOfPermissions,
              ...x
            }) => (
            <DropdownListItem
              component={"button"}
              {...x}
              key={id}
            />
          ))
        }
      </Dropdown>
      {
        filteredActions.filter(x => x.additionalContent)
          .map(({ additionalContent, id }) => (
            <Fragment key={id}>
              {additionalContent}
            </Fragment>
          ))
      }
    </>
  );
});